<script setup lang="ts">
import { storeToRefs } from 'pinia'

import { BtnOrange } from '~/components/ui/Buttons/'
import VBookmark from '~/components/ui/VBookmark.vue'
import VText from '~/components/ui/VText.vue'

import { useMiscResizeStore } from '~/stores/miscResize'
import { useUserStore } from '~/stores/user'

import { capitalize } from '~/utils/strings'

import { Breakpoints } from '~/enums/breakpoints'

import type { Influencer } from '~/types/influencer'

type NavigationTextConfig =
  | { cfg: 'sans/16/normal'; color: 'black' }
  | { cfg: 'sans/16/normal'; color: 'gray-500' }
  | { cfg: 'sans/16/medium'; color: 'orange-500' }
  | { cfg: 'sans/16/medium'; color: 'orange-500' }

type Props = {
  modelValue: boolean
  influencer: Influencer
  showAbout: boolean
  hasInteraction: boolean
  showInteraction: boolean
  userIsOwner: boolean
}

type Emits = {
  toggle: []
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
})

const emit = defineEmits<Emits>()

const { t } = useI18n()
const localePath = useLocalePath()
const { handleChevronClick } = useCuratorProfilePreviousButton()

const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())
const { IS_BAND, IS_LOGGED_IN } = storeToRefs(useUserStore())

const isBookmarkDisplayed = computed<boolean>(() => {
  return (
    !props.userIsOwner &&
    IS_BAND.value &&
    SCREEN_WIDTH.value >= Breakpoints.md &&
    !props.influencer?.is_scout &&
    props.influencer?.visible
  )
})

const isInteractionsTabDisplayed = computed<boolean>(() => {
  return IS_LOGGED_IN.value && IS_BAND.value
})
const aboutSelectorText = computed<string>(() => {
  const baseI18nSelector = 'influencer.profile.slug.profile'
  const result =
    SCREEN_WIDTH.value < Breakpoints.md
      ? t(`influencer.profile.slug.profile.nav00`)
      : t(`influencer.profile.slug.profile.nav01`)

  return capitalize(result)
})

const aboutSelectorTextConfig = computed<NavigationTextConfig>(() => {
  return props.showAbout
    ? { cfg: 'sans/16/medium', color: 'orange-500' }
    : { cfg: 'sans/16/normal', color: 'black' }
})

const interactionSelectorText = computed<string>(() => {
  let result

  if (SCREEN_WIDTH.value < Breakpoints.sm) {
    result = props.hasInteraction
      ? t(`influencer.profile.slug.profile.nav02`)
      : t(`influencer.profile.slug.profile.nav07`)
  } else if (SCREEN_WIDTH.value < Breakpoints.md) {
    result = props.hasInteraction
      ? t(`influencer.profile.slug.profile.nav03`)
      : t(`influencer.profile.slug.profile.nav07`)
  } else {
    result = props.hasInteraction
      ? t(`influencer.profile.slug.profile.nav04`, {
          name: props.influencer.entity,
        })
      : t(`influencer.profile.slug.profile.nav08`, {
          name: props.influencer.entity,
        })
  }

  return capitalize(result)
})

const interactionSelectorTextConfig = computed<NavigationTextConfig>(() => {
  if (!props.hasInteraction) {
    return { cfg: 'sans/16/normal', color: 'gray-500' }
  } else {
    return props.showInteraction
      ? { cfg: 'sans/16/medium', color: 'orange-500' }
      : { cfg: 'sans/16/normal', color: 'black' }
  }
})

async function leaveLink() {
  if (props.userIsOwner) {
    await navigateTo(
      localePath(`/influencer/profile/${props.influencer.slug}/edit/`),
    )
  }
}
</script>

<template>
  <div class="tw-bg-white tw-shadow">
    <div class="mainParentContainer">
      <button
        v-if="!userIsOwner && SCREEN_WIDTH >= Breakpoints.lg"
        type="button"
        :aria-label="$t('common.goBack')"
        @click="handleChevronClick"
      >
        <i
          class="fas fa-chevron-left icon tw-absolute tw-cursor-pointer tw-text-gray-500"
          aria-hidden
        />
      </button>
      <div
        class="wrapper tw-flex tw-h-full tw-items-center"
        :class="{ padded: userIsOwner }"
      >
        <div v-if="!userIsOwner" class="tw-flex tw-w-full">
          <div class="selectorWrapper">
            <div class="selectorElem" @click="emit('toggle')">
              <VText v-bind="aboutSelectorTextConfig" class="tw-inline-block">{{
                aboutSelectorText
              }}</VText>
            </div>
            <transition name="scale" mode="out-in">
              <div v-if="showAbout" class="redBar" />
            </transition>
          </div>
          <div
            :class="{ noInteraction: !hasInteraction }"
            class="selectorWrapper"
            @click="emit('toggle')"
          >
            <div v-if="isInteractionsTabDisplayed" class="selectorElem">
              <VText
                v-bind="interactionSelectorTextConfig"
                class="tw-inline-block"
                >{{ interactionSelectorText }}</VText
              >
            </div>
            <transition name="scale" mode="out-in">
              <div v-if="showInteraction" class="redBar" />
            </transition>
          </div>
        </div>
        <BtnOrange v-if="userIsOwner" @click="leaveLink">
          <span class="btnText">{{
            $t('influencer.profile.slug.profile.nav05')
          }}</span>
        </BtnOrange>
      </div>
      <VBookmark
        v-if="isBookmarkDisplayed"
        :influencer="influencer"
        :hide-tooltip-arrow="true"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.padded {
  @apply tw-justify-center tw-py-4;

  @screen lg {
    @apply tw-justify-end;
  }
}

.selectorElem {
  @apply tw-inline-block tw-w-full tw-cursor-pointer tw-p-6 tw-text-center;
  .noInteraction {
    @apply tw-cursor-default;
  }

  @screen lg {
    @apply tw-w-auto tw-py-8;
  }
}

.redBar {
  @apply tw-absolute tw-w-full tw-bg-orange-500;
  bottom: 0px;
  right: 0;
  height: 2px;
  @screen lg {
    right: -24px;
    width: calc(100% + 48px);
  }
}

.wrapper {
  @apply tw-w-full;
  @screen lg {
    margin-left: calc(160px + 24px);
  }
}

.icon {
  @apply tw-text-lg;

  &.fa-bookmark {
    @apply tw-ml-auto tw-inline-block tw-text-orange-500;
  }

  &.fas.fa-chevron-left {
    margin-left: -32px;
  }
}

.mainParentContainer {
  @apply tw-flex tw-items-center;
}

.selectorWrapper {
  @apply tw-relative tw-flex tw-w-full tw-items-center;

  @screen lg {
    @apply tw-w-auto;
  }
}
</style>
